import React, { useState, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import { formatCurrency, shortMonths } from '../../utils/formatters';

// ── Simulador de quitação de dívidas ────────────────────────────────────
// Compara "só as parcelas" vs "parcelas + aporte extra mensal" usando os
// métodos Bola de Neve (menor saldo primeiro — vitórias rápidas) ou
// Avalanche (maior juros primeiro — economiza mais). Juros = % a.m. da dívida.
//
// Regras da simulação (mês a mês, teto de 600 meses):
// 1. Juros do mês entram no saldo de cada dívida viva
// 2. Cada dívida recebe sua parcela mínima
// 3. O aporte extra + as parcelas de dívidas JÁ QUITADAS (efeito bola de neve)
//    vão pra dívida-alvo da vez, na ordem do método escolhido
function simulate(debts, extra, strategy, roll) {
  const ds = debts.map(d => ({
    id: d.id,
    name: d.name,
    remaining: d.remaining || 0,
    installment: d.installment || 0,
    rate: (parseFloat(d.interestRate) || 0) / 100,
  }));
  const orderFn = strategy === 'avalanche'
    ? (a, b) => b.rate - a.rate || a.remaining - b.remaining
    : (a, b) => a.remaining - b.remaining;

  let months = 0;
  let totalInterest = 0;
  const payoffMonth = {};

  while (ds.some(d => d.remaining > 0.005) && months < 600) {
    months++;
    let freed = 0;
    ds.forEach(d => {
      if (d.remaining > 0.005) {
        const juros = d.remaining * d.rate;
        d.remaining += juros;
        totalInterest += juros;
      } else if (roll) {
        freed += d.installment; // parcela liberada engrossa a bola de neve
      }
    });
    ds.forEach(d => {
      if (d.remaining > 0.005) d.remaining = Math.max(0, d.remaining - d.installment);
    });
    let budget = extra + freed;
    const alive = ds.filter(d => d.remaining > 0.005).sort(orderFn);
    for (const d of alive) {
      if (budget <= 0) break;
      const pay = Math.min(budget, d.remaining);
      d.remaining -= pay;
      budget -= pay;
    }
    ds.forEach(d => {
      if (d.remaining <= 0.005 && payoffMonth[d.id] == null) payoffMonth[d.id] = months;
    });
  }

  return {
    months,
    totalInterest: Math.round(totalInterest * 100) / 100,
    payoffMonth,
    finished: !ds.some(d => d.remaining > 0.005),
  };
}

const monthLabel = (offset) => {
  const d = new Date();
  d.setMonth(d.getMonth() + offset);
  return `${shortMonths[d.getMonth()]}/${d.getFullYear()}`;
};

export default function DebtSimulator() {
  const { state } = useApp();
  const [extra, setExtra] = useState('200');
  const [strategy, setStrategy] = useState('snowball');
  const [open, setOpen] = useState(false);

  const activeDebts = (state.debts || []).filter(d => !d.settled && (d.remaining || 0) > 0);

  const result = useMemo(() => {
    if (!activeDebts.length) return null;
    const extraVal = parseFloat(extra) || 0;
    const base = simulate(activeDebts, 0, strategy, false);
    const plan = simulate(activeDebts, extraVal, strategy, true);
    return { base, plan, extraVal };
  }, [activeDebts, extra, strategy]); // eslint-disable-line

  if (!activeDebts.length) return null;

  const { base, plan, extraVal } = result;
  const mesesGanhos = base.finished && plan.finished ? base.months - plan.months : null;
  const jurosEconomizados = base.finished && plan.finished ? base.totalInterest - plan.totalInterest : null;
  const ordered = [...activeDebts].sort((a, b) => (plan.payoffMonth[a.id] ?? 999) - (plan.payoffMonth[b.id] ?? 999));

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(29,158,117,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className="fas fa-route" style={{ color: 'var(--green)', fontSize: 15 }}></i>
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14 }}>Simulador de Quitação</div>
            <div style={{ fontSize: 11, color: 'var(--muted)' }}>quando você fica livre das dívidas — e como acelerar</div>
          </div>
        </div>
        <i className={`fas fa-chevron-${open ? 'up' : 'down'}`} style={{ color: 'var(--muted)', fontSize: 13 }}></i>
      </div>

      {open && (
        <div style={{ marginTop: 16 }}>
          {/* Controles */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 14 }}>
            <div style={{ flex: '1 1 160px' }}>
              <label className="form-label">Aporte extra por mês (R$)</label>
              <input className="form-control" type="number" min="0" step="50" value={extra} onChange={e => setExtra(e.target.value)} />
            </div>
            <div style={{ flex: '2 1 220px' }}>
              <label className="form-label">Método</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className={`btn btn-sm ${strategy === 'snowball' ? 'btn-primary' : 'btn-ghost'}`} style={{ flex: 1, justifyContent: 'center' }} onClick={() => setStrategy('snowball')} title="Quita a menor dívida primeiro — vitórias rápidas motivam">
                  ⛄ Bola de Neve
                </button>
                <button className={`btn btn-sm ${strategy === 'avalanche' ? 'btn-primary' : 'btn-ghost'}`} style={{ flex: 1, justifyContent: 'center' }} onClick={() => setStrategy('avalanche')} title="Ataca o maior juros primeiro — economiza mais dinheiro">
                  🏔️ Avalanche
                </button>
              </div>
            </div>
          </div>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 14 }}>
            {strategy === 'snowball'
              ? '⛄ Bola de Neve: quita a MENOR dívida primeiro — cada quitação libera a parcela pra próxima, e as vitórias rápidas mantêm a motivação.'
              : '🏔️ Avalanche: ataca o MAIOR juros primeiro — matematicamente é o que economiza mais dinheiro.'}
          </div>

          {/* Comparação */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
            <div style={{ background: 'var(--surface2)', borderRadius: 10, padding: '12px 14px' }}>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>Só as parcelas de hoje</div>
              {base.finished ? (
                <>
                  <div style={{ fontWeight: 800, fontSize: 16 }}>{base.months} meses <span style={{ fontWeight: 500, fontSize: 12, color: 'var(--muted)' }}>({monthLabel(base.months)})</span></div>
                  <div style={{ fontSize: 12, color: 'var(--red)', marginTop: 2 }}>{formatCurrency(base.totalInterest)} de juros</div>
                </>
              ) : (
                <div style={{ fontSize: 12, color: 'var(--red)', fontWeight: 700 }}>⚠️ Não quita — os juros crescem mais que as parcelas</div>
              )}
            </div>
            <div style={{ background: 'rgba(29,158,117,0.08)', border: '1px solid rgba(29,158,117,0.25)', borderRadius: 10, padding: '12px 14px' }}>
              <div style={{ fontSize: 11, color: 'var(--green)', marginBottom: 4, fontWeight: 600 }}>Com +{formatCurrency(extraVal)}/mês</div>
              {plan.finished ? (
                <>
                  <div style={{ fontWeight: 800, fontSize: 16, color: 'var(--green)' }}>{plan.months} meses <span style={{ fontWeight: 500, fontSize: 12, color: 'var(--muted)' }}>({monthLabel(plan.months)})</span></div>
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{formatCurrency(plan.totalInterest)} de juros</div>
                </>
              ) : (
                <div style={{ fontSize: 12, color: 'var(--red)', fontWeight: 700 }}>⚠️ Ainda não quita — aumente o aporte</div>
              )}
            </div>
          </div>

          {mesesGanhos != null && extraVal > 0 && plan.finished && (
            <div style={{ background: 'rgba(201,162,39,0.08)', border: '1px solid rgba(201,162,39,0.3)', borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: 13, fontWeight: 600, color: 'var(--gold)' }}>
              🎉 Você fica livre {mesesGanhos} {mesesGanhos === 1 ? 'mês' : 'meses'} antes e economiza {formatCurrency(Math.max(0, jurosEconomizados))} em juros!
            </div>
          )}

          {/* Ordem de ataque */}
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 6 }}>
            <i className="fas fa-list-ol" style={{ marginRight: 6 }}></i>Plano de ataque ({strategy === 'snowball' ? 'menor saldo primeiro' : 'maior juros primeiro'}):
          </div>
          {ordered.map((d, i) => (
            <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: i < ordered.length - 1 ? '1px solid var(--border)' : 'none', fontSize: 13 }}>
              <span style={{ width: 22, height: 22, borderRadius: '50%', background: 'var(--surface2)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{i + 1}º</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontWeight: 600 }}>{d.name}</span>
                <span style={{ color: 'var(--muted)', fontSize: 12 }}> · {formatCurrency(d.remaining)}{d.interestRate > 0 ? ` · ${d.interestRate}% a.m.` : ''}</span>
              </div>
              <span style={{ fontSize: 12, color: plan.payoffMonth[d.id] != null ? 'var(--green)' : 'var(--red)', fontWeight: 600, flexShrink: 0 }}>
                {plan.payoffMonth[d.id] != null ? `quitada em ${monthLabel(plan.payoffMonth[d.id])}` : 'não quita'}
              </span>
            </div>
          ))}
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 10 }}>
            Simulação aproximada: juros compostos mensais sobre o saldo devedor, parcelas de hoje mantidas e parcelas de dívidas quitadas somadas ao aporte da próxima.
          </div>
        </div>
      )}
    </div>
  );
}
